import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';

import { AccountService, AlertService } from '@app/_services';

@Component({
    template: `
        <div class="login-wrapper">
            <div class="login-card">
                <div class="login-header">
                    <img src="assets/AllianceInsuranceWebsite.png" alt="Alliance Assure" class="login-logo">
                    <h2 class="mt-3 mb-1" style="font-weight: 700; color: #1E293B;">Create Account</h2>
                    <p class="text-muted mb-0">Register for Alliance Assure Portal</p>
                </div>
                <form [formGroup]="form" (ngSubmit)="onSubmit()">
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label for="firstName" class="form-label">First Name</label>
                            <input type="text" id="firstName" formControlName="firstName" class="form-control"
                                   [class.is-invalid]="submitted && f.firstName.errors"
                                   placeholder="Enter first name"
                                   aria-describedby="firstNameError">
                            <div id="firstNameError" class="invalid-feedback" *ngIf="submitted && f.firstName.errors">
                                <span *ngIf="f.firstName.errors.required">First Name is required</span>
                            </div>
                        </div>
                        <div class="col-md-6 mb-3">
                            <label for="lastName" class="form-label">Last Name</label>
                            <input type="text" id="lastName" formControlName="lastName" class="form-control"
                                   [class.is-invalid]="submitted && f.lastName.errors"
                                   placeholder="Enter last name"
                                   aria-describedby="lastNameError">
                            <div id="lastNameError" class="invalid-feedback" *ngIf="submitted && f.lastName.errors">
                                <span *ngIf="f.lastName.errors.required">Last Name is required</span>
                            </div>
                        </div>
                    </div>
                    <div class="mb-3">
                        <label for="username" class="form-label">Username</label>
                        <input type="text" id="username" formControlName="username" class="form-control"
                               [class.is-invalid]="submitted && f.username.errors"
                               placeholder="Choose a username"
                               aria-describedby="usernameError">
                        <div id="usernameError" class="invalid-feedback" *ngIf="submitted && f.username.errors">
                            <span *ngIf="f.username.errors.required">Username is required</span>
                        </div>
                    </div>
                    <div class="mb-3">
                        <label for="password" class="form-label">Password</label>
                        <input type="password" id="password" formControlName="password" class="form-control"
                               [class.is-invalid]="submitted && f.password.errors"
                               placeholder="Create a password (min 6 chars)"
                               aria-describedby="passwordError">
                        <div id="passwordError" class="invalid-feedback" *ngIf="submitted && f.password.errors">
                            <span *ngIf="f.password.errors.required">Password is required</span>
                            <span *ngIf="f.password.errors.minlength">Password must be at least 6 characters</span>
                        </div>
                    </div>
                    <div *ngIf="error" class="alert alert-danger py-2 mb-3" role="alert">
                        <i class="fa fa-exclamation-circle me-1"></i> {{ error }}
                    </div>
                    <button [disabled]="loading" class="btn btn-primary w-100 py-2 mb-3">
                        <span *ngIf="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Register
                    </button>
                    <div class="text-center">
                        <a routerLink="../login" class="text-decoration-none">Already have an account? Sign in</a>
                    </div>
                </form>
            </div>
        </div>
    `
})
export class RegisterComponent implements OnInit {
    form!: UntypedFormGroup;
    loading = false;
    submitted = false;
    error = '';

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private accountService: AccountService,
        private alertService: AlertService
    ) { }

    ngOnInit() {
        this.form = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            username: ['', Validators.required],
            password: ['', [Validators.required, Validators.minLength(6)]]
        });
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;
        this.error = '';
        this.accountService.register(this.form.value)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.alertService.success('Registration successful', { keepAfterRouteChange: true });
                    this.router.navigate(['../login'], { relativeTo: this.route });
                },
                error: error => {
                    this.error = error;
                    this.alertService.error(error);
                    this.loading = false;
                }
            });
    }
}